import React, { useEffect, useState } from 'react'
import { Calendar } from 'primereact/calendar'
import './../form.scss'
import { PropsType } from '../../../models/util'
import { addLocale } from 'primereact/api'

export interface DateRangePickerProps extends PropsType {
    onChangeCallback: (e: any) => void // on change callback
    value: { start: Date; end: Date } // date range values

    // to override primereact and/or wardrobe styles, use custom classes
    classes?: string // css classes for the element
    containerClasses?: string // css classes for the <div> containers (start and end)
    containerClasses2?: string // css classes for the 'end' <div> container (if specified overrides container classes for the end input)
    style?: any

    mandatory?: boolean // adds * in labels (TODO validation message)
    disabled?: boolean // if true the element is disabled
    colWidth?: number | string // set element width from 1 to 12 columns (default: 12)

    label?: string // start date label
    label2?: string // end date label
    floatingLabel?: boolean // floating labels appear only when the field is filled in
    placeholder?: string // start date placeholder
    placeholder2?: string // end date placeholder

    maxDate?: Date // maximum date (for start and end date)
    minDate?: Date // minimum date (for start and end date)
    maxStartDate?: Date // maximum date for start date
    minEndDate?: Date // minimum date for end date
    firstDayOfWeek?: number // first day of the week in month table

    additionalProps?: any // pass props for the Prime React original component

    singleInput?: boolean // render date range picker with type range (single input field)

    // the calendar icon is not customizable
}

const DateRangePicker = (props: DateRangePickerProps) => {
    const {
        onChangeCallback,
        value,
        mandatory,
        placeholder,
        placeholder2,
        colWidth,
        disabled,
        additionalProps,
        containerClasses,
        containerClasses2,
        floatingLabel,
        label,
        label2,
        minDate,
        maxDate,
        maxStartDate,
        minEndDate,
        firstDayOfWeek,
        singleInput,
    } = props

    let today = new Date()
    let year = today.getFullYear()
    const [startViewDate, setStartViewDate] = useState<any>(today)
    const [endViewDate, setEndViewDate] = useState<any>(today)
    const [dates, setDates] = useState<Date[] | null>(null) // value for singleInput Calendar

    useEffect(() => {
        if (!singleInput) {
            if (minDate) {
                setStartViewDate(minDate)
            }
        }
    }, [minDate])

    useEffect(() => {
        if (singleInput) {
            if (dates?.[0] && dates?.[1]) {
                // se ci sono entrambe le date selezionate
                // chiamo l'onChangeCallback per la chiamata
                onChangeCallback({ start: dates[0], end: dates[1] })
            }
        }
    }, [dates])

    useEffect(() => {
        if (singleInput) {
            if (
                value &&
                value.start &&
                value.end &&
                (value.start !== dates?.[0] || value.end !== dates?.[1])
            ) {
                // se dal value arrivano entrambi start e end e uno dei due
                // è diverso da quelli di state, setto i nuovi
                setDates([value.start, value.end])
            } else if (value && value.start === null && value.end === null) {
                // altrimenti se start e end arrivano null (causa clear filters)
                // aggiorno lo state con i null
                setDates(null)
            }
        }
    }, [value])

    // set first day of the week
    addLocale('custom', {
        firstDayOfWeek: firstDayOfWeek ? firstDayOfWeek : 0,
    })

    // the min date for the end field is the max between min end date from props and selected start date
    let _minEndDate = value?.start

    if (minEndDate) {
        _minEndDate = value?.start > minEndDate ? value?.start : minEndDate
    }

    // define placeholders
    // placeholder prop populates the start date placeholder or single input placeholder
    // placeholder2 prop populates the end date placeholder
    const _placeholderStart = placeholder ? placeholder : 'Select Start Date'
    const _placeholderEnd = placeholder2 ? placeholder2 : 'Select End Date'
    const _placeholderRange = placeholder
        ? placeholder
        : 'Select a range of dates'

    // define labels
    // label prop populates the start date label
    // label2 prop populates the end date label
    const _labelStart = label ? label : 'Start Date'
    const _labelEnd = label2 ? label2 : 'End Date'

    // define default container classes
    const _containerClasses = containerClasses ? containerClasses : ''

    if (singleInput) {
        return (
            <div
                className={`wardrobe-form-input p-col-${
                    colWidth ? colWidth : '12'
                } ${_containerClasses} ${label ? 'labeled-input' : ''} `}>
                <Calendar
                    value={dates}
                    locale="custom"
                    panelClassName="wardrobe-datepicker"
                    monthNavigator
                    icon="cu cu-calendar"
                    yearNavigator
                    yearRange={`1970:${year + 5}`}
                    placeholder={_placeholderRange}
                    showButtonBar
                    showIcon
                    className={`wardrobe-calendar w-100`}
                    selectionMode="range"
                    disabled={disabled}
                    dateFormat="yy/mm/dd"
                    readOnlyInput
                    appendTo={document.body}
                    baseZIndex={2000}
                    maxDate={maxStartDate || maxDate}
                    minDate={minDate}
                    onChange={(e) => {
                        if (Array.isArray(e.target.value)) {
                            setDates(e.target.value)
                        } else {
                            setDates(null)
                        }
                    }}
                    onHide={() => {
                        // se una delle due date è undefined
                        // quando nascondo il pannello azzero il valore
                        if (!dates?.[0] || !dates?.[1]) {
                            setDates(null)
                            onChangeCallback({ start: null, end: null })
                        }
                    }}
                />
                {label && (
                    <label
                        className={`${floatingLabel && !value ? 'empty' : ''}`}>
                        {`${_labelStart}${mandatory ? ' *' : ''}`}
                    </label>
                )}
            </div>
        )
    } else {
        return (
            <>
                <div
                    className={`wardrobe-form-input p-col-${
                        colWidth ? colWidth : '12'
                    } ${_containerClasses} ${label ? 'labeled-input' : ''} `}>
                    <Calendar
                        panelClassName="wardrobe-datepicker"
                        locale="custom"
                        monthNavigator
                        yearNavigator
                        yearRange={`1970:${year + 5}`}
                        baseZIndex={2000}
                        showButtonBar
                        icon="cu cu-calendar"
                        showIcon
                        className={`wardrobe-calendar w-100`}
                        value={value?.start}
                        disabled={disabled}
                        dateFormat="yy/mm/dd"
                        selectionMode="single"
                        placeholder={_placeholderStart}
                        appendTo={document.body}
                        viewDate={startViewDate}
                        minDate={minDate}
                        maxDate={maxStartDate || maxDate}
                        onChange={(e) => {
                            // each time start date is selected, end date is cleared
                            onChangeCallback({ start: e.value, end: undefined })
                            if (e.value) setEndViewDate(e.value)
                            else setEndViewDate(today)
                        }}
                        onViewDateChange={(e) => {
                            setStartViewDate(e.value)
                        }}
                        {...additionalProps}
                    />
                    {label && (
                        <label
                            className={`${
                                floatingLabel && !value ? 'empty' : ''
                            }`}>
                            {`${_labelStart}${mandatory ? ' *' : ''}`}
                        </label>
                    )}
                </div>
                <div
                    className={`wardrobe-form-input p-col-${
                        colWidth ? colWidth : '12'
                    } ${
                        containerClasses2
                            ? containerClasses2
                            : _containerClasses
                    } ${label ? 'labeled-input' : ''}`}>
                    <Calendar
                        panelClassName="wardrobe-datepicker"
                        locale="custom"
                        monthNavigator
                        yearNavigator
                        yearRange={`1970:${year + 5}`}
                        baseZIndex={2000}
                        showButtonBar
                        showIcon
                        icon="cu cu-calendar"
                        value={value?.end}
                        disabled={!value?.start || disabled}
                        className={`wardrobe-calendar w-100`}
                        appendTo={document.body}
                        dateFormat="yy/mm/dd"
                        selectionMode="single"
                        placeholder={_placeholderEnd}
                        minDate={_minEndDate}
                        maxDate={maxDate}
                        viewDate={endViewDate}
                        onViewDateChange={(e) => {
                            setEndViewDate(e.value)
                        }}
                        onChange={(e) =>
                            // confirm start date, set end date
                            onChangeCallback({
                                start: value?.start,
                                end: e.value,
                            })
                        }
                        {...additionalProps}
                    />
                    {label2 && (
                        <label
                            className={`${
                                floatingLabel && !value ? 'empty' : ''
                            }`}>
                            {`${_labelEnd}${mandatory ? ' *' : ''}`}
                        </label>
                    )}
                </div>
            </>
        )
    }
}

export default DateRangePicker
